// ============================================================================
// 🆕 NOVOCONTATOMODAL — NativeSheet · buscar / criar cliente (V4)
// ============================================================================

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Search, UserPlus, Phone, Loader2, AlertCircle, ArrowRight,
} from 'lucide-react';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { useClientes } from '../../hooks/useClientes';
import { validateTelefone } from '../../utils/validators';
import { formatContactName } from '../../utils/formatters';
import NativeSheet from '../../../../components/ui/NativeSheet';

const NovoContatoModal = ({ isOpen, onClose, onSelectClient }) => {
  const isMobile = useIsMobile();

  const resolvedUserId = useMemo(() => {
    try {
      const stored = localStorage.getItem('auth_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.id) return parsed.id;
      }
    } catch {}
    try {
      if (window.firebase?.auth?.currentUser?.uid) {
        return window.firebase.auth.currentUser.uid;
      }
    } catch {}
    return null;
  }, []);

  const { allClients, loading, searchClients, createClient } = useClientes({
    userId: resolvedUserId,
  });

  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('search');
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setMode('search');
      setForm({ name: '', phone: '', email: '' });
      setFormError('');
      setTimeout(() => searchInputRef.current?.focus(), 120);
    }
  }, [isOpen]);

  const filteredClients = (searchClients?.(search) || allClients || []).filter((c) => c.phone || c.name);

  const handleSelectClient = useCallback((client) => {
    onSelectClient?.(client);
    onClose?.();
  }, [onSelectClient, onClose]);

  const handleCreate = useCallback(async () => {
    setFormError('');
    if (!form.name.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }
    const phoneValidation = validateTelefone(form.phone);
    if (!phoneValidation.valid) {
      setFormError(phoneValidation.error || 'Telefone inválido');
      return;
    }
    try {
      setCreating(true);
      const newClient = await createClient({
        name: form.name.trim(),
        phone: form.phone,
        email: form.email.trim(),
      });
      onSelectClient?.(newClient);
      onClose?.();
    } catch (err) {
      setFormError(err.message || 'Erro ao criar cliente');
    } finally {
      setCreating(false);
    }
  }, [form, createClient, onSelectClient, onClose]);

  const inputCls = `surface-input w-full outline-none ${isMobile ? 'px-3 py-2.5 text-[16px]' : 'px-3.5 py-2.5 text-sm'}`;

  return (
    <NativeSheet
      open={!!isOpen}
      onClose={onClose}
      title={mode === 'search' ? 'Nova Conversa' : 'Novo Contato'}
      maxWidth="max-w-md"
      fullHeightMobile
      footer={
        mode === 'create' ? (
          <div className={`flex gap-2 ${isMobile ? 'flex-col-reverse' : 'justify-end'}`}>
            <button
              type="button"
              onClick={() => setMode('search')}
              className={`rounded-xl font-medium border border-color text-secondary hover:bg-hover ${
                isMobile ? 'w-full py-3 text-sm' : 'px-4 py-2.5 text-sm'
              }`}
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className={`rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-500 shadow-md flex items-center justify-center gap-2 disabled:opacity-50 ${
                isMobile ? 'w-full py-3 text-sm' : 'px-5 py-2.5 text-sm'
              }`}
            >
              {creating ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
              {creating ? 'Criando…' : 'Criar e conversar'}
            </button>
          </div>
        ) : null
      }
    >
      {/* Tabs */}
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-muted border border-color mb-3">
        {[
          { id: 'search', label: 'Buscar cliente' },
          { id: 'create', label: 'Novo contato' },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setMode(tab.id)}
            className={`py-2 rounded-lg text-xs font-semibold transition ${
              mode === tab.id
                ? tab.id === 'create'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-blue-600 text-white shadow-sm'
                : 'text-secondary hover:bg-hover'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {mode === 'search' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 bg-muted border border-color rounded-xl px-3 py-2.5">
            <Search size={16} className="text-muted flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              className="flex-1 bg-transparent text-sm outline-none text-primary placeholder:text-muted"
            />
          </div>

          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-1">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-muted" />
              </div>
            ) : filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-muted gap-3">
                <Search size={28} className="opacity-50" />
                <p className="text-sm">Nenhum cliente encontrado</p>
                <button
                  type="button"
                  onClick={() => setMode('create')}
                  className="text-blue-600 text-sm font-semibold"
                >
                  Criar novo contato
                </button>
              </div>
            ) : (
              filteredClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleSelectClient(client)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-hover text-left active:scale-[0.99] transition"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-xs">
                      {(client.name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-primary truncate">
                      {formatContactName(client.name)}
                    </p>
                    {client.phone && (
                      <p className="text-xs text-muted flex items-center gap-1">
                        <Phone size={10} />
                        {client.phone}
                      </p>
                    )}
                  </div>
                  <ArrowRight size={16} className="text-muted flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {mode === 'create' && (
        <div className="space-y-3.5">
          <div>
            <label className="block text-xs font-semibold text-secondary mb-1.5">Nome *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Nome completo"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary mb-1.5">Telefone *</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              placeholder="(11) 99999-9999"
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-secondary mb-1.5">Email (opcional)</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
              placeholder="cliente@email.com"
              className={inputCls}
            />
          </div>
          {formError && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-500/10 rounded-xl px-3 py-2.5">
              <AlertCircle size={16} className="flex-shrink-0" />
              {formError}
            </div>
          )}
        </div>
      )}
    </NativeSheet>
  );
};

export { NovoContatoModal };
export default NovoContatoModal;
